import { Box, TextField, Typography, useTheme } from '@mui/material';
import { ComponentProps, ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList, UseRefinementListProps } from 'react-instantsearch';

import { ExpandButton } from '~/components/ExpandButton/ExpandButton.tsx';
import { SearchRefinementListItem } from '~/routes/Search/components/SearchRefinementListItem/SearchRefinementListItem.tsx';

import { Ul } from './SearchRefinementList.styled.tsx';

interface SearchRefinementListProps<C extends ElementType> extends UseRefinementListProps {
  title: string;
  itemComponent: C;
  itemComponentProps?: ComponentProps<C>;
}

const SEARCH_ENABLED = false;

export const SearchRefinementList = <C extends ElementType>({
  title,
  itemComponent,
  itemComponentProps,
  ...props
}: SearchRefinementListProps<C>) => {
  /*
    The search right now can only search in the value delivered by meili search
    If there are real strings like "open | close | etc" it works fine. But these
    strings are usually a short list, so there is no need to search through them

    For e.g. supplier names it would be nice, but: meili just holds the uuids not
    the real names. So we cant you the out of the box search for this.
  */

  const theme = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchRefinementList' });
  const { items, refine, searchForItems, canToggleShowMore, isShowingMore, toggleShowMore } = useRefinementList({
    ...props,
    limit: 5, // limit when to show more button
    showMore: true,
  });

  const noResults = items.length === 0;

  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Box
        sx={{
          paddingTop: 0,
        }}
      >
        {SEARCH_ENABLED ? (
          <TextField
            fullWidth={true}
            type="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={t('search', { title })}
            onChange={(event) => searchForItems(event.currentTarget.value)}
            sx={{ backgroundColor: theme.palette.common.white }}
          />
        ) : null}
        {noResults ? (
          <Typography variant="body2" color="textSecondary">
            {t('noResults')}
          </Typography>
        ) : (
          <Ul>
            {items.map((item, index) => {
              // there is a bug for generic component types in regards to emotion
              // therefore we need to cast the component to a ElementType<any> here
              // https://github.com/emotion-js/emotion/issues/2169
              const ItemComponent: ElementType = itemComponent || SearchRefinementListItem;
              return <ItemComponent key={index} {...itemComponentProps} item={item} refine={refine} />;
            })}
          </Ul>
        )}

        {canToggleShowMore ? (
          <ExpandButton
            isExpanded={isShowingMore}
            expandMoreTitle={t('showMore')}
            expandLessTitle={t('showLess')}
            onToggle={toggleShowMore}
          />
        ) : null}
      </Box>
    </Box>
  );
};
